import Axios from "axios";
import { DrugSource } from "@/models/Drug/DrugProduct";

// Patient education sheet sections for on-screen reference, mirroring the content that
// prints on the patient education handout. Sourced from GET /Drug/{drugId}/{source}/patient-education,
// a thin wrapper over the same PrintingHelper the print flow uses. See SD-4306.

// Mirrors CPHub.Data.ViewModels.PrintPatEdViewModel (camelCase JSON).
export interface PatientEducationSheet {
    descriptionHeader?: string | null;
    description?: string | null;
    descriptionFooter?: string | null;
    contraindicationsHeader?: string | null;
    contraindications?: string | null;
    contraindicationsFooter?: string | null;
    administrationHeader?: string | null;
    administration?: string | null;
    administrationFooter?: string | null;
    interactionsHeader?: string | null;
    interactions?: string | null;
    interactionsFooter?: string | null;
    missedDoseHeader?: string | null;
    missedDose?: string | null;
    missedDoseFooter?: string | null;
    monitoringHeader?: string | null;
    monitoring?: string | null;
    monitoringFooter?: string | null;
    sideEffectsHeader?: string | null;
    sideEffects?: string | null;
    sideEffectsFooter?: string | null;
    storageHeader?: string | null;
    storage?: string | null;
    storageFooter?: string | null;
    ndc?: string | null;
    formattedNDC?: string | null;
}

/** A single displayable section of the patient education sheet. */
export interface PatientEducationSection {
    header: string;
    body: string;
    footer: string;
}

/** Fetches the patient education sheet sections for a drug identified by (drugId, source). */
export async function fetchPatientEducation(drugId: number, source: DrugSource): Promise<PatientEducationSheet> {
    const { data } = await Axios.get<PatientEducationSheet>(`/Drug/${drugId}/${source}/patient-education`);
    return data;
}

/**
 * Flattens the sheet DTO into the ordered list of non-empty sections to render.
 * Order mirrors the printed handout, with Interactions inserted after Administration.
 */
export function toSections(sheet: PatientEducationSheet | null): PatientEducationSection[] {
    if (!sheet) return [];
    const sections: PatientEducationSection[] = [
        { header: sheet.descriptionHeader ?? "", body: sheet.description ?? "", footer: sheet.descriptionFooter ?? "" },
        { header: sheet.contraindicationsHeader ?? "", body: sheet.contraindications ?? "", footer: sheet.contraindicationsFooter ?? "" },
        { header: sheet.administrationHeader ?? "", body: sheet.administration ?? "", footer: sheet.administrationFooter ?? "" },
        { header: sheet.interactionsHeader ?? "", body: sheet.interactions ?? "", footer: sheet.interactionsFooter ?? "" },
        { header: sheet.missedDoseHeader ?? "", body: sheet.missedDose ?? "", footer: sheet.missedDoseFooter ?? "" },
        { header: sheet.monitoringHeader ?? "", body: sheet.monitoring ?? "", footer: sheet.monitoringFooter ?? "" },
        { header: sheet.sideEffectsHeader ?? "", body: sheet.sideEffects ?? "", footer: sheet.sideEffectsFooter ?? "" },
        { header: sheet.storageHeader ?? "", body: sheet.storage ?? "", footer: sheet.storageFooter ?? "" },
    ];
    // A section is worth showing only when it has a header or body.
    return sections.filter(s => s.header.trim() !== "" || s.body.trim() !== "");
}
